/** A field error as consumed by `@verclam/form-validator`'s `updateValidationFromExternalSource`. */
export type FieldError = { field: string; message: string };

/** The shape the API actually sends: one message per field, keyed by field name. */
export type ApiErrorMessage = {
	message: string;
	fieldErrors: Record<string, string>;
};

/** An error body is unvalidated JSON: it is read through guards, never assumed to match `ApiErrorMessage`. */
type ApiErrorBody = Record<string, unknown>;

const isFieldErrorsRecord = (candidate: unknown): candidate is Record<string, string> => {
	if (typeof candidate !== 'object' || candidate === null || Array.isArray(candidate)) {
		return false;
	}

	return Object.values(candidate).every((value) => typeof value === 'string');
};

class ApiError extends Error {
	status: number;
	body: ApiErrorBody | undefined;

	constructor(message: string, data: { status: number; body?: ApiErrorBody }) {
		super(message);

		this.name = 'ApiError';
		this.status = data.status;
		this.body = data.body;
	}

	hasErrors(): boolean {
		return this.hasErrorMessage() || this.hasFieldErrors();
	}

	hasErrorMessage(): boolean {
		return typeof this.body?.message === 'string';
	}

	hasFieldErrors(): boolean {
		const fieldErrors = this.body?.fieldErrors;

		return isFieldErrorsRecord(fieldErrors) && Object.keys(fieldErrors).length > 0;
	}

	get errorMessage(): string | undefined {
		const message = this.body?.message;

		return typeof message === 'string' ? message : undefined;
	}

	/**
	 * The API sends field errors as a `Record<string, string>`, converted here to the
	 * `Array<{ field, message }>` shape `updateValidationFromExternalSource` expects, so every
	 * caller consumes the same array without reconverting it itself.
	 */
	get fieldErrors(): FieldError[] | undefined {
		const fieldErrors = this.body?.fieldErrors;

		if (!isFieldErrorsRecord(fieldErrors)) {
			return undefined;
		}

		return Object.entries(fieldErrors).map(([field, message]) => ({ field, message }));
	}
}

export default ApiError;
